import type {
  Dialog,
  DialogFilter,
  Document,
  DocumentAttribute,
  Message,
  MessageEntity,
  MessagesDialogs,
  MessagesMessages,
  Peer,
  MessageMedia,
  Photo,
  ReactionCount,
  User
} from '@layer';

import type {
  ChatNotesAttachment,
  ChatNotesFolder,
  ChatNotesFormatting,
  ChatNotesNote,
  ChatNotesUser
} from './contract';

const SYNTHETIC_PEER_ID_BASE = 4_000_000_000_000_000;
const SYNTHETIC_UNFILED_PEER_ID = SYNTHETIC_PEER_ID_BASE - 1;
const MAX_UUID_PEER_SUFFIX = 0xffffffffffff;

export const CHAT_NOTES_UNFILED_FOLDER_ID = 'unfiled';

export interface ChatNotesWebKDialogSource {
  folders: ChatNotesFolder[];
  notes: ChatNotesNote[];
  user: ChatNotesUser;
}

function uuidPrefixAsNumber(id: string, hexadecimalCharacters: number): number {
  return Number.parseInt(id.replace(/-/g, '').slice(0, hexadecimalCharacters), 16);
}

export function folderIdToWebKPeerId(folderId: string | typeof CHAT_NOTES_UNFILED_FOLDER_ID): number {
  if(folderId === CHAT_NOTES_UNFILED_FOLDER_ID) return SYNTHETIC_UNFILED_PEER_ID;
  return SYNTHETIC_PEER_ID_BASE + (uuidPrefixAsNumber(folderId, 12) % MAX_UUID_PEER_SUFFIX);
}

function assertUniqueMappedIds<T>(
  values: T[],
  sourceId: (value: T) => string,
  mappedId: (value: T) => number,
  kind: string
): void {
  const owners = new Map<number, string>();
  for(const value of values) {
    const source = sourceId(value);
    const mapped = mappedId(value);
    const owner = owners.get(mapped);
    if(owner && owner !== source) {
      throw new Error(`Chat Notes ${kind} ID collision between ${owner} and ${source}.`);
    }
    owners.set(mapped, source);
  }
}

function peerUser(userId: string | number): Peer.peerUser {
  return {_: 'peerUser', user_id: userId};
}

function emptyNotifySettings() {
  return {_: 'peerNotifySettings' as const, pFlags: {}};
}

export function chatNotesUserToWebKUser(user: ChatNotesUser): User.user {
  return {
    _: 'user',
    id: user.telegramUserId,
    pFlags: {
      self: true,
      premium: user.isPremium || undefined
    },
    first_name: user.firstName,
    last_name: user.lastName,
    username: user.username,
    lang_code: user.languageCode
  };
}

export function chatNotesFolderToWebKUser(folder: ChatNotesFolder): User.user {
  return {
    _: 'user',
    id: folderIdToWebKPeerId(folder.id),
    access_hash: 0,
    pFlags: {contact: true},
    first_name: folder.title
  };
}

export function chatNotesFolderToWebKDialogFilter(folder: ChatNotesFolder): DialogFilter.dialogFilter {
  const peer = {
    _: 'inputPeerUser' as const,
    user_id: folderIdToWebKPeerId(folder.id),
    access_hash: 0
  };
  return {
    _: 'dialogFilter',
    pFlags: {},
    id: folder.folderNumber,
    title: {_: 'textWithEntities', text: folder.title, entities: []},
    emoticon: folder.icon,
    pinned_peers: [],
    include_peers: [peer],
    exclude_peers: []
  };
}

export function chatNotesUnfiledToWebKUser(): User.user {
  return {
    _: 'user',
    id: SYNTHETIC_UNFILED_PEER_ID,
    access_hash: 0,
    pFlags: {contact: true},
    first_name: 'Notes'
  };
}

const SIMPLE_ENTITY_TYPES = new Set([
  'bold', 'italic', 'code', 'underline', 'strike', 'spoiler', 'blockquote',
  'url', 'email', 'phone', 'hashtag', 'cashtag'
]);

export function chatNotesFormattingToWebKEntity(formatting: ChatNotesFormatting): MessageEntity {
  if(formatting.type === 'text_url') {
    return {
      _: 'messageEntityTextUrl',
      offset: formatting.offset,
      length: formatting.length,
      url: formatting.data ?? ''
    };
  }
  if(formatting.type === 'pre') {
    return {
      _: 'messageEntityPre',
      offset: formatting.offset,
      length: formatting.length,
      language: formatting.data ?? ''
    };
  }
  if(SIMPLE_ENTITY_TYPES.has(formatting.type)) {
    const suffix = formatting.type === 'strike' ? 'Strike' :
      formatting.type.split('_').map((part) => part[0].toUpperCase() + part.slice(1)).join('');
    return {
      _: `messageEntity${suffix}`,
      offset: formatting.offset,
      length: formatting.length
    } as MessageEntity;
  }
  return {_: 'messageEntityUnknown', offset: formatting.offset, length: formatting.length};
}

export function webKEntityToChatNotesFormatting(entity: MessageEntity): ChatNotesFormatting | undefined {
  const common = {offset: entity.offset, length: entity.length};
  switch(entity._) {
    case 'messageEntityBold': return {type: 'bold', ...common};
    case 'messageEntityItalic': return {type: 'italic', ...common};
    case 'messageEntityCode': return {type: 'code', ...common};
    case 'messageEntityUnderline': return {type: 'underline', ...common};
    case 'messageEntityStrike': return {type: 'strike', ...common};
    case 'messageEntitySpoiler': return {type: 'spoiler', ...common};
    case 'messageEntityBlockquote': return {type: 'blockquote', ...common};
    case 'messageEntityUrl': return {type: 'url', ...common};
    case 'messageEntityEmail': return {type: 'email', ...common};
    case 'messageEntityPhone': return {type: 'phone', ...common};
    case 'messageEntityHashtag': return {type: 'hashtag', ...common};
    case 'messageEntityCashtag': return {type: 'cashtag', ...common};
    case 'messageEntityTextUrl': return {type: 'text_url', data: entity.url, ...common};
    case 'messageEntityPre': return {type: 'pre', data: entity.language, ...common};
    default: return undefined;
  }
}

function reactionCounts(reactions: string[]): ReactionCount[] {
  return reactions.map((emoticon, chosen_order) => ({
    _: 'reactionCount',
    chosen_order,
    reaction: {_: 'reactionEmoji', emoticon},
    count: 1
  }));
}

export function chatNotesNoteToWebKMessage(
  note: ChatNotesNote,
  selfUserId: string,
  noteNumbersById: ReadonlyMap<string, number> = new Map(),
  attachmentsById: ReadonlyMap<string, ChatNotesAttachment> = new Map()
): Message.message {
  const folderId = note.folderId ?? CHAT_NOTES_UNFILED_FOLDER_ID;
  const replyToMessageNumber = note.replyToNoteId && noteNumbersById.get(note.replyToNoteId);
  const forwardedMessageNumber = note.forwardedFromNoteId && noteNumbersById.get(note.forwardedFromNoteId);
  return {
    _: 'message',
    id: note.messageNumber,
    pFlags: {out: true, pinned: note.isPinned || undefined},
    from_id: peerUser(selfUserId),
    peer_id: peerUser(folderIdToWebKPeerId(folderId)),
    date: Math.floor(Date.parse(note.createdAt) / 1000),
    edit_date: note.updatedAt === note.createdAt ? undefined : Math.floor(Date.parse(note.updatedAt) / 1000),
    message: note.content,
    grouped_id: note.groupId,
    entities: note.formatting.map(chatNotesFormattingToWebKEntity),
    reply_to: replyToMessageNumber ? {
      _: 'messageReplyHeader',
      pFlags: {},
      reply_to_msg_id: replyToMessageNumber
    } : undefined,
    fwd_from: forwardedMessageNumber ? {
      _: 'messageFwdHeader',
      pFlags: {saved_out: true},
      from_id: peerUser(selfUserId),
      date: Math.floor(Date.parse(note.createdAt) / 1000),
      saved_from_peer: peerUser(selfUserId),
      saved_from_msg_id: forwardedMessageNumber
    } : undefined,
    reactions: note.reactions.length ? {
      _: 'messageReactions',
      pFlags: {can_see_list: true},
      results: reactionCounts(note.reactions)
    } : undefined,
    media: note.attachmentId && attachmentsById.has(note.attachmentId) ?
      chatNotesAttachmentToWebKMedia(attachmentsById.get(note.attachmentId)!) : undefined
  };
}

function attachmentAttributes(attachment: ChatNotesAttachment): DocumentAttribute[] {
  const attributes: DocumentAttribute[] = [{
    _: 'documentAttributeFilename',
    file_name: attachment.fileName
  }];
  if(attachment.kind === 'video' || attachment.kind === 'video_note') {
    attributes.push({
      _: 'documentAttributeVideo',
      pFlags: {
        round_message: attachment.kind === 'video_note' || undefined,
        supports_streaming: true
      },
      duration: attachment.duration ?? 0,
      w: attachment.width ?? 0,
      h: attachment.height ?? 0
    });
  }
  if(attachment.kind === 'audio' || attachment.kind === 'voice') {
    attributes.push({
      _: 'documentAttributeAudio',
      pFlags: {voice: attachment.kind === 'voice' || undefined},
      duration: attachment.duration ?? 0
    });
  }
  if(attachment.kind === 'animation') attributes.push({_: 'documentAttributeAnimated'});
  return attributes;
}

export function chatNotesAttachmentToWebKMedia(attachment: ChatNotesAttachment): MessageMedia {
  const reference = new TextEncoder().encode(attachment.id);
  if(attachment.kind === 'photo') {
    const photo: Photo.photo = {
      _: 'photo',
      pFlags: {},
      id: attachment.id,
      access_hash: 0,
      file_reference: reference,
      date: Math.floor(Date.parse(attachment.createdAt) / 1000),
      sizes: [{
        _: 'photoSize',
        type: 'x',
        w: attachment.width ?? 0,
        h: attachment.height ?? 0,
        size: attachment.size
      }],
      dc_id: 1
    };
    return {_: 'messageMediaPhoto', pFlags: {}, photo};
  }
  const document: Document.document = {
    _: 'document',
    id: attachment.id,
    access_hash: 0,
    file_reference: reference,
    date: Math.floor(Date.parse(attachment.createdAt) / 1000),
    dc_id: 1,
    attributes: attachmentAttributes(attachment),
    pFlags: {},
    size: attachment.size,
    mime_type: attachment.mimeType as Document.document['mime_type'],
    file_name: attachment.fileName,
    duration: attachment.duration,
    w: attachment.width,
    h: attachment.height,
    type: attachment.kind === 'video_note' ? 'round' :
      attachment.kind === 'animation' ? 'gif' :
      attachment.kind === 'document' ? undefined : attachment.kind
  };
  return {
    _: 'messageMediaDocument',
    pFlags: {
      video: attachment.kind === 'video' || undefined,
      round: attachment.kind === 'video_note' || undefined,
      voice: attachment.kind === 'voice' || undefined
    },
    document
  };
}

function folderToDialog(folderId: string, topMessageId: number): Dialog.dialog {
  return {
    _: 'dialog',
    pFlags: {},
    peer: peerUser(folderIdToWebKPeerId(folderId)),
    top_message: topMessageId,
    read_inbox_max_id: topMessageId,
    read_outbox_max_id: topMessageId,
    unread_count: 0,
    unread_mentions_count: 0,
    unread_reactions_count: 0,
    unread_poll_votes_count: 0,
    notify_settings: emptyNotifySettings()
  };
}

function newestNote(notes: ChatNotesNote[]): ChatNotesNote | undefined {
  return notes.reduce<ChatNotesNote | undefined>((newest, note) => {
    if(!newest) return note;
    const byDate = Date.parse(note.createdAt) - Date.parse(newest.createdAt);
    return byDate > 0 || (byDate === 0 && note.id > newest.id) ? note : newest;
  }, undefined);
}

export function buildChatNotesWebKDialogs(
  source: ChatNotesWebKDialogSource,
  attachmentsById: ReadonlyMap<string, ChatNotesAttachment> = new Map()
): MessagesDialogs.messagesDialogs {
  assertUniqueMappedIds(source.folders, (folder) => folder.id, (folder) => folderIdToWebKPeerId(folder.id), 'folder');
  assertUniqueMappedIds(source.notes, (note) => note.id, (note) => note.messageNumber, 'note');

  const notesByFolder = new Map<string, ChatNotesNote[]>();
  for(const note of source.notes) {
    const folderId = note.folderId ?? CHAT_NOTES_UNFILED_FOLDER_ID;
    const notes = notesByFolder.get(folderId) ?? [];
    notes.push(note);
    notesByFolder.set(folderId, notes);
  }

  const folderSources = [
    ...source.folders.map((folder) => ({id: folder.id, user: chatNotesFolderToWebKUser(folder)})),
    {id: CHAT_NOTES_UNFILED_FOLDER_ID, user: chatNotesUnfiledToWebKUser()}
  ];
  const foldersWithTopNotes = folderSources.map((folderSource) => ({
    ...folderSource,
    topNote: newestNote(notesByFolder.get(folderSource.id) ?? [])
  }));
  const topNotes = foldersWithTopNotes
  .map(({topNote}) => topNote)
  .filter((note): note is ChatNotesNote => !!note);
  const noteNumbersById = new Map(source.notes.map((note) => [note.id, note.messageNumber]));

  return {
    _: 'messages.dialogs',
    dialogs: foldersWithTopNotes.map(({id, topNote}) =>
      folderToDialog(id, topNote?.messageNumber ?? 0)),
    messages: topNotes.map((note) => chatNotesNoteToWebKMessage(
      note,
      source.user.telegramUserId,
      noteNumbersById,
      attachmentsById
    )),
    chats: [],
    users: [chatNotesUserToWebKUser(source.user), ...folderSources.map(({user}) => user)]
  };
}

export function buildChatNotesWebKHistory(
  user: ChatNotesUser,
  folderId: string | typeof CHAT_NOTES_UNFILED_FOLDER_ID,
  notes: ChatNotesNote[],
  attachmentsById: ReadonlyMap<string, ChatNotesAttachment> = new Map()
): MessagesMessages.messagesMessages {
  const folderNotes = notes.filter((note) => (note.folderId ?? CHAT_NOTES_UNFILED_FOLDER_ID) === folderId);
  assertUniqueMappedIds(folderNotes, (note) => note.id, (note) => note.messageNumber, 'note');
  const noteNumbersById = new Map(notes.map((note) => [note.id, note.messageNumber]));

  return {
    _: 'messages.messages',
    messages: folderNotes
    .slice()
    .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt) || right.id.localeCompare(left.id))
    .map((note) => chatNotesNoteToWebKMessage(
      note,
      user.telegramUserId,
      noteNumbersById,
      attachmentsById
    )),
    topics: [],
    chats: [],
    users: [chatNotesUserToWebKUser(user)]
  };
}

export function buildChatNotesWebKMessages(
  user: ChatNotesUser,
  notes: ChatNotesNote[],
  attachmentsById: ReadonlyMap<string, ChatNotesAttachment> = new Map()
): MessagesMessages.messagesMessages {
  assertUniqueMappedIds(notes, (note) => note.id, (note) => note.messageNumber, 'note');
  const noteNumbersById = new Map(notes.map((note) => [note.id, note.messageNumber]));
  return {
    _: 'messages.messages',
    messages: notes.map((note) => chatNotesNoteToWebKMessage(
      note,
      user.telegramUserId,
      noteNumbersById,
      attachmentsById
    )),
    topics: [],
    chats: [],
    users: [chatNotesUserToWebKUser(user)]
  };
}
